import { useState } from 'react';

export default function MenuBeverageTrends({ data, onChange }) {
  const menuAnalysis = data?.menuAnalysis || {};
  const beverageAnalysis = data?.beverageAnalysis || {};
  const menuTrends = data?.menuTrends || data?.marketData?.menuTrends || {};

  const updateMenu = (field, value) => {
    const updated = { ...data, menuAnalysis: { ...menuAnalysis, [field]: value } };
    if (onChange) onChange(updated);
  };

  const updateBeverage = (field, value) => {
    const updated = { ...data, beverageAnalysis: { ...beverageAnalysis, [field]: value } };
    if (onChange) onChange(updated);
  };

  const updateMenuList = (field, index, value) => {
    const list = [...(menuAnalysis[field] || [''])];
    list[index] = value;
    updateMenu(field, list);
  };

  const updateBevList = (field, index, value) => {
    const list = [...(beverageAnalysis[field] || [''])];
    list[index] = value;
    updateBeverage(field, list);
  };

  const addToList = (type, field) => {
    if (type === 'menu') {
      const list = [...(menuAnalysis[field] || []), ''];
      updateMenu(field, list);
    } else {
      const list = [...(beverageAnalysis[field] || []), ''];
      updateBeverage(field, list);
    }
  };

  return (
    <div className="card p-6">
      <h3 className="font-display text-lg text-white mb-1">Menu & Beverage Trend Intelligence</h3>
      <p className="text-xs text-neutral-500 mb-5">Current market trends vs this concept's alignment</p>

      <div className="grid sm:grid-cols-2 gap-6">
        {/* Food / Menu */}
        <div>
          <h4 className="text-sm font-semibold text-brand-gold mb-3 uppercase tracking-wider">Food & Menu</h4>

          <div className="mb-3">
            <p className="text-xs text-neutral-500 mb-1">Trend Alignment</p>
            <textarea
              value={menuAnalysis.trendAlignment || ''}
              onChange={(e) => updateMenu('trendAlignment', e.target.value)}
              placeholder="How well does the menu align with current trends?"
              rows={2}
              className="input-field text-sm"
            />
          </div>

          <div className="mb-3">
            <p className="text-xs text-neutral-500 mb-1">Menu Engineering Notes</p>
            <textarea
              value={menuAnalysis.engineeringNotes || ''}
              onChange={(e) => updateMenu('engineeringNotes', e.target.value)}
              placeholder="Stars, plowhorses, puzzles, dogs..."
              rows={2}
              className="input-field text-sm"
            />
          </div>

          <div className="mb-3">
            <p className="text-xs text-emerald-400 font-semibold mb-1.5">Strengths</p>
            {(menuAnalysis.strengths || ['']).map((s, i) => (
              <input key={i} type="text" value={s} onChange={(e) => updateMenuList('strengths', i, e.target.value)}
                placeholder="Menu strength..." className="input-field text-sm mb-1.5" />
            ))}
            <button onClick={() => addToList('menu', 'strengths')} className="btn-ghost text-xs">+ Add</button>
          </div>

          <div className="mb-3">
            <p className="text-xs text-red-400 font-semibold mb-1.5">Weaknesses</p>
            {(menuAnalysis.weaknesses || ['']).map((w, i) => (
              <input key={i} type="text" value={w} onChange={(e) => updateMenuList('weaknesses', i, e.target.value)}
                placeholder="Menu weakness..." className="input-field text-sm mb-1.5" />
            ))}
            <button onClick={() => addToList('menu', 'weaknesses')} className="btn-ghost text-xs">+ Add</button>
          </div>

          {menuTrends.food?.length > 0 && menuTrends.food[0] && (
            <div>
              <p className="text-xs text-neutral-500 font-semibold mb-1.5">Market Food Trends</p>
              <div className="flex flex-wrap gap-1.5">
                {menuTrends.food.filter(Boolean).map((t, i) => (
                  <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-brand-gold/10 text-brand-gold border border-brand-gold/20">{t}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Beverage */}
        <div>
          <h4 className="text-sm font-semibold text-brand-teal mb-3 uppercase tracking-wider">Beverage Program</h4>

          <div className="mb-3">
            <p className="text-xs text-neutral-500 mb-1">Trend Alignment</p>
            <textarea
              value={beverageAnalysis.trendAlignment || ''}
              onChange={(e) => updateBeverage('trendAlignment', e.target.value)}
              placeholder="How well does the program align with trends?"
              rows={2}
              className="input-field text-sm"
            />
          </div>

          <div className="mb-3">
            <p className="text-xs text-neutral-500 mb-1">Program Notes</p>
            <textarea
              value={beverageAnalysis.programNotes || ''}
              onChange={(e) => updateBeverage('programNotes', e.target.value)}
              placeholder="Spirit selection, cocktail program, wine list..."
              rows={2}
              className="input-field text-sm"
            />
          </div>

          <div className="mb-3">
            <p className="text-xs text-emerald-400 font-semibold mb-1.5">Strengths</p>
            {(beverageAnalysis.strengths || ['']).map((s, i) => (
              <input key={i} type="text" value={s} onChange={(e) => updateBevList('strengths', i, e.target.value)}
                placeholder="Beverage strength..." className="input-field text-sm mb-1.5" />
            ))}
            <button onClick={() => addToList('bev', 'strengths')} className="btn-ghost text-xs">+ Add</button>
          </div>

          <div className="mb-3">
            <p className="text-xs text-red-400 font-semibold mb-1.5">Weaknesses</p>
            {(beverageAnalysis.weaknesses || ['']).map((w, i) => (
              <input key={i} type="text" value={w} onChange={(e) => updateBevList('weaknesses', i, e.target.value)}
                placeholder="Beverage weakness..." className="input-field text-sm mb-1.5" />
            ))}
            <button onClick={() => addToList('bev', 'weaknesses')} className="btn-ghost text-xs">+ Add</button>
          </div>

          {menuTrends.beverage?.length > 0 && menuTrends.beverage[0] && (
            <div>
              <p className="text-xs text-neutral-500 font-semibold mb-1.5">Market Beverage Trends</p>
              <div className="flex flex-wrap gap-1.5">
                {menuTrends.beverage.filter(Boolean).map((t, i) => (
                  <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-brand-teal/10 text-brand-teal border border-brand-teal/20">{t}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
